import React, { useEffect, useState, useRef } from 'react';

const GexProfileOverlay = ({ gexLevels, chartRef, mainSeriesRef, chartContainerRef }) => {
    const [bars, setBars] = useState([]);
    const containerRef = useRef(null);
    const intervalRef = useRef(null);

    useEffect(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }

        const profile = gexLevels?.profile;
        if (!profile || profile.length === 0) return;

        const maxGex = Math.max(...profile.map(p => Math.abs(p.gex)));
        if (maxGex === 0) return;

        const updatePositions = () => {
            const series = mainSeriesRef?.current;
            const container = chartContainerRef?.current;
            if (!series || !container) return;

            try {
                const containerWidth = container.clientWidth;
                const maxWidth = containerWidth * 0.25;

                const newBars = profile.map(p => {
                    const y = series.priceToCoordinate(Number(p.strike));
                    if (y === null || y === undefined) return null;
                    
                    const width = (Math.abs(p.gex) / maxGex) * maxWidth;
                    
                    return {
                        strike: p.strike,
                        gex: p.gex,
                        y,
                        width
                    };
                }).filter(b => b !== null);
                
                setBars(newBars);
            } catch (err) {
                // silently retry
            }
        };

        updatePositions();
        intervalRef.current = setInterval(updatePositions, 200);

        let timeScale;
        const subscribeWhenReady = setInterval(() => {
            if (chartRef?.current) {
                if (typeof chartRef.current.timeScale === 'function') {
                    timeScale = chartRef.current.timeScale();
                    timeScale.subscribeVisibleLogicalRangeChange(updatePositions);
                    clearInterval(subscribeWhenReady);
                } else if (typeof chartRef.current.on === 'function') {
                    chartRef.current.on('viewport:changed', updatePositions);
                    clearInterval(subscribeWhenReady);
                }
            }
        }, 200);

        let resizeObserver;
        if (chartContainerRef?.current) {
            resizeObserver = new ResizeObserver(updatePositions);
            resizeObserver.observe(chartContainerRef.current);
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            clearInterval(subscribeWhenReady);
            try {
                if (timeScale) timeScale.unsubscribeVisibleLogicalRangeChange(updatePositions);
                if (chartRef?.current?.off) chartRef.current.off('viewport:changed', updatePositions);
            } catch(e) {}
            if (resizeObserver) resizeObserver.disconnect();
        };

    }, [gexLevels]);

    if (bars.length === 0) return null;

    // Format GEX as dollar value: $2.0M, $696K, etc.
    const formatGex = (val) => {
        const abs = Math.abs(val);
        if (abs >= 1000000) return '$' + (abs / 1000000).toFixed(1) + 'M';
        if (abs >= 1000) return '$' + (abs / 1000).toFixed(0) + 'K';
        return '$' + abs.toString();
    };

    // Identify key levels
    const callWall = gexLevels?.call_wall;
    const putWall = gexLevels?.put_wall;
    const zeroGamma = gexLevels?.zero_gamma;

    const getKeyLabel = (strike) => {
        if (strike === callWall) return 'CALL WALL';
        if (strike === putWall) return 'PUT WALL';
        if (strike === zeroGamma) return 'ZERO γ';
        return null;
    };

    // Get source badge styling and date
    const source = gexLevels?.source_type || 'synthetic';
    const dateStr = gexLevels?.formatted_date || gexLevels?.date || '';

    const getSourceBadge = () => {
        if (source === 'synthetic') {
            return (
                <div 
                    className="absolute top-2 left-2 px-1.5 py-0.5 rounded text-[8px] font-bold select-none z-30 flex items-center gap-1"
                    style={{
                        backgroundColor: 'rgba(249,115,22,0.15)',
                        color: '#fdba74',
                        border: '1px solid rgba(249,115,22,0.4)',
                        textShadow: '0 0 4px rgba(0,0,0,0.9)',
                        backdropFilter: 'blur(4px)',
                        fontFamily: 'sans-serif',
                        letterSpacing: '0.05em'
                    }}
                >
                    ⚠️ SYNTHETIC GEX (OFFLINE FALLBACK)
                </div>
            );
        }
        if (source === 'fallback') {
            return (
                <div 
                    className="absolute top-2 left-2 px-1.5 py-0.5 rounded text-[8px] font-bold select-none z-30 flex items-center gap-1"
                    style={{
                        backgroundColor: 'rgba(239,68,68,0.15)',
                        color: '#fca5a5',
                        border: '1px solid rgba(239,68,68,0.4)',
                        textShadow: '0 0 4px rgba(0,0,0,0.9)',
                        backdropFilter: 'blur(4px)',
                        fontFamily: 'sans-serif',
                        letterSpacing: '0.05em'
                    }}
                >
                    🚨 DEGRADED SPOT PRICE FALLBACK
                </div>
            );
        }
        if (source === 'live' || dateStr) {
            return (
                <div 
                    className="absolute top-2 left-2 px-1.5 py-0.5 rounded text-[8px] font-bold select-none z-30 flex items-center gap-1"
                    style={{
                        backgroundColor: 'rgba(16,185,129,0.15)',
                        color: '#a7f3d0',
                        border: '1px solid rgba(16,185,129,0.4)',
                        textShadow: '0 0 4px rgba(0,0,0,0.9)',
                        backdropFilter: 'blur(4px)',
                        fontFamily: 'sans-serif',
                        letterSpacing: '0.05em'
                    }}
                >
                    📅 GEX: {dateStr ? dateStr.toUpperCase() : 'LIVE'}
                </div>
            );
        }
        return null;
    };

    return (
        <div ref={containerRef} className="absolute inset-0 pointer-events-none z-20 overflow-hidden">
            {getSourceBadge()}
            {bars.map((bar, i) => {
                const keyLabel = getKeyLabel(bar.strike);
                const isPositive = bar.gex > 0;

                return (
                    <div
                        key={i}
                        className="absolute flex items-center pointer-events-none"
                        style={{
                            right: '60px', // Offset from the right to avoid covering the latest candles
                            top: `${bar.y - 2}px`,
                            height: '6px',
                            flexDirection: 'row-reverse',
                        }}
                    >
                        {/* The horizontal bar */}
                        <div
                            style={{
                                height: keyLabel ? '5px' : '2px',
                                width: `${Math.max(2, Math.min(80, bar.width))}px`,
                                backgroundColor: isPositive ? '#10b981' : '#ef4444',
                                opacity: keyLabel ? 0.65 : 0.15,
                                borderRadius: '1px 0 0 1px',
                            }}
                        />
                        {/* Key level badge & value label (ONLY for Call Wall / Put Wall / Zero Gamma) */}
                        {keyLabel && (
                            <div className="flex items-center gap-1 mr-1">
                                <span
                                    className="text-[9px] font-mono select-none font-bold"
                                    style={{
                                        color: isPositive ? '#6ee7b7' : '#fca5a5',
                                        textShadow: '0 0 4px rgba(0,0,0,0.9)',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {formatGex(bar.gex)}
                                </span>
                                <span
                                    className="text-[8px] font-bold px-1 py-0.2 rounded select-none shadow-sm"
                                    style={{
                                        backgroundColor: keyLabel === 'CALL WALL' ? 'rgba(16,185,129,0.35)' 
                                            : keyLabel === 'PUT WALL' ? 'rgba(239,68,68,0.35)' 
                                            : 'rgba(251,191,36,0.35)',
                                        color: keyLabel === 'CALL WALL' ? '#a7f3d0' 
                                            : keyLabel === 'PUT WALL' ? '#fecdd3' 
                                            : '#fef08a',
                                        border: `1px solid ${keyLabel === 'CALL WALL' ? 'rgba(16,185,129,0.6)' 
                                            : keyLabel === 'PUT WALL' ? 'rgba(239,68,68,0.6)' 
                                            : 'rgba(245,158,11,0.6)'}`,
                                        whiteSpace: 'nowrap',
                                        backdropFilter: 'blur(4px)',
                                        textShadow: '0 0 4px rgba(0,0,0,0.9)',
                                    }}
                                >
                                    {keyLabel}
                                </span>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default GexProfileOverlay;
